// Shared message protocol between main thread and worker.
//
// The worker hosts the emulator (stub today, MAME-WASM in Phase B). The
// protocol is intentionally narrow so the stub and the real WASM are
// drop-in replacements for each other.

export const LCD_WIDTH = 160;
export const LCD_HEIGHT = 100;
export const LCD_PIXELS = LCD_WIDTH * LCD_HEIGHT;
export const FRAME_BYTES = LCD_PIXELS * 4;

/** Messages main → worker. */
export type MainToWorker =
  | { readonly type: 'power-on' }
  | { readonly type: 'power-off' }
  | { readonly type: 'reset' }
  | { readonly type: 'keydown'; readonly key: string; readonly sdlScancode?: number }
  | { readonly type: 'keyup'; readonly key: string; readonly sdlScancode?: number }
  | { readonly type: 'rf-rx'; readonly byte: number; readonly peerId?: string }
  | { readonly type: 'console-rx'; readonly text: string }
  | { readonly type: 'console-rx-bytes'; readonly bytes: ArrayBuffer }
  | { readonly type: 'dump-flash'; readonly requestId: string };

/** Messages worker → main. */
export type WorkerToMain =
  | { readonly type: 'ready' }
  | { readonly type: 'state'; readonly state: 'off' | 'booting' | 'running' }
  | { readonly type: 'frame'; readonly width: number; readonly height: number; readonly buffer: ArrayBuffer }
  | { readonly type: 'rf-tx'; readonly byte: number }
  | { readonly type: 'console-tx'; readonly byte: number }
  | { readonly type: 'flash-dump'; readonly requestId: string; readonly buffer: ArrayBuffer }
  | { readonly type: 'flash-dump-error'; readonly requestId: string; readonly message: string }
  | { readonly type: 'log'; readonly message: string };

/**
 * Cybiko LCD palette. The real hardware is 4-bit greyscale (16 shades);
 * for the stub we use 4 levels that match the rendered cream backdrop
 * of the device photo.
 */
export const PALETTE = {
  bg: [184, 196, 160, 255], // #b8c4a0 — matches .lcd background
  light: [138, 150, 118, 255], // mid-light
  dark: [77, 86, 64, 255], // mid-dark
  ink: [40, 42, 38, 255], // near-black
} as const;

export type PaletteShade = keyof typeof PALETTE;
